// Centro de ayuda del local: como pedir desde el celular, como corregir un
// pedido antes de que lo confirmen, como instalar la app en el telefono, como
// habilitar los avisos y como se entra (con Google, sin clave).
//
// Mismo formato que el centro de ayuda de administracion (src/app/pages/ayuda.ts):
// pantallas simuladas + paso a paso + "bueno saber".

import { iconoAyuda, iconoRuta, iconoCampana } from "../iconos";
import {
  estadoAvisos,
  activarAvisos,
  textoAvisos,
  estiloAvisos,
  estaInstalada,
} from "../notificaciones";

interface Seccion {
  hash: string;
  titulo: string;
  queEs: string;
  mock: string;
  pasos: string[];
  tips: string[];
}

const SECCIONES: Seccion[] = [
  {
    hash: "#/pedir",
    titulo: "Hacer un pedido",
    queEs:
      "Es la pantalla principal. Aparecen solo los artículos que la confitería tiene habilitados para tu local, con TUS precios, agrupados por categoría.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex items-center justify-between"><span class="etiqueta mb-0">Fecha de entrega</span><span class="mock-campo">17/07/2026</span></div>
        <div class="mock-campo w-full">🔍 Buscar un artículo...</div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="mb-1 flex items-center gap-2 font-bold text-masa-700">▾ SANDWICHERÍA <span class="rounded-full bg-acento-600 px-1.5 text-xs text-white">2</span></div>
          <div class="flex items-center justify-between gap-2">
            <span>Olimpico blanco<br><span class="text-xs text-masa-400">$ 78</span></span>
            <span class="flex items-center gap-1"><span class="mock-btn-sec">−</span><span class="mock-campo">6</span><span class="mock-btn-sec">+</span></span>
            <span class="font-bold">$ 468</span>
          </div>
        </div>
        <div class="flex items-center justify-between border-t border-crema-200 pt-2">
          <span>TOTAL ESTIMADO<br><b>$ 468</b></span>
          <span class="flex gap-1"><span class="mock-btn-sec">Limpiar</span><span class="mock-btn">Enviar pedido</span></span>
        </div>
      </div>`,
    pasos: [
      "Revisá la <b>fecha de entrega</b> arriba. Viene puesta para <b>mañana</b>, que es lo normal; cambiala solo si pedis para otro día. Para atras no se puede: lo más viejo que acepta es <b>hoy</b>.",
      "Elegí el <b>turno</b>: mañana o tarde. La cuadra separa la producción del día en esos dos bloques.",
      "Tocá una <b>categoría</b> para abrirla (Sandwichería, Confitería, Ensaladas…), o escribí el nombre del artículo en el <b>buscador</b> de arriba: lo elegís de la lista y la app te <b>abre la categoría y te marca el artículo</b> en amarillo, con el teclado listo para poner la cantidad.",
      "Cargá las cantidades con los botones <b>−</b> y <b>+</b>, o escribi el número directo en el casillero.",
      "El <b>total</b> se va actualizando abajo, siempre a la vista.",
      "Cuando está todo, tocá <b>Enviar pedido</b>.",
    ],
    tips: [
      "El pedido a medio hacer <b>no se pierde</b>: si cerrás la app o se corta internet, cuando volvés está como lo dejaste.",
      "Si ya mandaste un pedido para <b>esa misma fecha y ese mismo turno</b>, la app te avisa antes de mandar otro y te muestra cuál es. Podés ir a corregir aquel, o mandar este igual si es algo que falto.",
      "Apenas se manda, la pantalla te muestra <b>el detalle completo</b> de lo que pediste, para repasarlo ahí mismo.",
      "El numerito al lado del nombre de la categoría te dice <b>cuantos artículos cargaste</b> ahí adentro, sin abrirla.",
      "El buscador no necesita tildes ni el nombre entero: <b>gales</b> encuentra \"Torta galés\" y <b>olimpico bl</b> encuentra \"Olimpico blanco\". La fila queda marcada mientras cargás, aunque el teclado te tape media pantalla.",
      "Los <b>scones se piden por bandeja</b> (16 unidades cada una), no por unidad.",
      "Si manejás <b>más de un local</b>, arriba aparece un selector de <b>Local</b>: elegilo antes de cargar nada. Cada local tiene su catálogo, sus precios y su propio pedido a medio hacer, así que al cambiar de local la pantalla se rearma entera.",
      "Si un artículo no aparece, es que la confitería todavia no le puso precio para tu local: avisales.",
    ],
  },
  {
    hash: "#/mispedidos",
    titulo: "Mis pedidos",
    queEs:
      "El historial de lo que pediste, con el estado de cada uno. Mientras un pedido este PENDIENTE lo podés corregir o cancelar vos mismo.",
    mock: `
      <div class="mock space-y-2">
        <div class="flex flex-wrap gap-1"><span class="mock-btn">Recientes</span><span class="mock-btn-sec">Día</span><span class="mock-btn-sec">Semana</span><span class="mock-btn-sec">Mes</span><span class="mock-btn-sec">Todo</span></div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="flex items-center justify-between">
            <span><b>Entrega 17/07/2026 · Mañana</b><br><span class="text-xs text-masa-400">Pedido #12 · mandado el 16/07</span></span>
            <span class="rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">Pendiente</span>
          </div>
          <div class="mt-1 flex items-center justify-between">
            <span class="font-bold">$ 1.080</span>
            <span class="flex gap-1"><span class="mock-btn-sec">Ver detalle</span><span class="mock-btn-sec">Corregir</span><span class="mock-btn-sec">Cancelar</span></span>
          </div>
        </div>
        <div class="rounded-lg border border-crema-200 p-2">
          <div class="flex items-center justify-between">
            <span><b>Entrega 16/07/2026</b><br><span class="text-xs text-masa-400">Pedido #11</span></span>
            <span class="rounded-full bg-green-100 px-2 py-0.5 text-green-800">Confirmado</span>
          </div>
        </div>
      </div>`,
    pasos: [
      "Arranca en <b>Recientes</b>: los últimos pedidos, sin importar la fecha. Es lo que necesitás al entrar, incluido el que acabas de mandar.",
      "<b>Día</b>, <b>Semana</b> y <b>Mes</b> filtran por <b>fecha de entrega</b>: si ponés Día, ves lo que se entrega hoy y nada más. <b>Todo</b> muestra el historial completo.",
      "<b>Pendiente</b> (amarillo) = lo mandaste y la confitería todavia no lo confirmo. Se puede tocar.",
      "<b>Confirmado</b> (verde) = ya está aceptado y entra en la producción del día. Ya no se puede modificar desde el celular.",
      "<b>Cancelado</b> (gris) = lo cancelaste vos, o la confitería lo rechazo o lo anuló.",
      "<b>Corregir</b> vuelve a abrir el pedido con las cantidades que pusiste: cambias lo que haga falta y tocas <b>Guardar cambios</b>.",
      "<b>Ver detalle</b> muestra artículo por artículo lo que pediste.",
    ],
    tips: [
      "Cada tarjeta dice la <b>fecha de entrega</b> en grande y, abajo, <b>cuando lo mandaste</b>: son dos días distintos, porque el pedido se manda antes de que te lo entreguen. Los filtros de fecha miran la <b>entrega</b>; para encontrar algo por cuando lo mandaste, usa <b>Recientes</b>.",
      "Si el pedido ya está <b>confirmado</b> y necesitás cambiar algo, hay que <b>llamar a la confitería</b>: desde la app ya no se puede.",
      "Cuando confirman tu pedido te llega un <b>aviso</b> y la lista se actualiza sola, sin salir y volver a entrar.",
      "Si la confitería <b>cancela</b> un pedido —incluso uno ya confirmado— también te llega un aviso y pasa a <b>Cancelado</b>. Es raro que pase, pero si te llega, no cuentes con esa mercadería: convení con ellos por teléfono.",
      "En <b>Mi cuenta</b> el pedido cancelado no desaparece: queda el movimiento y debajo un <b>Pedido anulado</b> que lo descuenta. Así podés ver que estuvo y que se dio de baja, en vez de que el importe se borre sin explicación.",
      "El precio que se cobra es el del <b>momento en que lo confirman</b>. Si hubo un aumento en el medio, el total puede cambiar un poco.",
    ],
  },
  {
    hash: "#/micuenta",
    titulo: "Mi cuenta",
    queEs:
      "El resumen de lo que se te mandó y de lo que la confitería registró: pedidos, pagos, devoluciones y faltantes, con el saldo al día.",
    mock: `
      <div class="mock space-y-2">
        <div class="text-center"><span class="text-xs uppercase text-masa-400">Saldo</span><br><b class="text-2xl">$ 4.999</b></div>
        <table class="w-full"><tr class="text-masa-400"><td>Fecha</td><td>Movimiento</td><td class="text-right">Monto</td></tr>
          <tr><td>16/07</td><td>Pedido <span class="text-masa-400">#12</span><br><span class="text-xs text-masa-400">Mañana · tocá para ver el detalle</span></td><td class="text-right">$ 9.999</td></tr>
          <tr><td>16/07</td><td>Pedido <span class="text-masa-400">#13</span><br><span class="text-xs text-masa-400">Tarde · tocá para ver el detalle</span></td><td class="text-right">$ 4.999</td></tr>
          <tr><td>16/07</td><td>Pago</td><td class="text-right">− $ 5.000</td></tr>
        </table>
      </div>`,
    pasos: [
      "Arriba de todo está el <b>saldo</b>.",
      "Abajo, cada movimiento con su fecha: los <b>pedidos</b> suman y los <b>pagos</b>, <b>devoluciones</b> y <b>faltantes</b> restan.",
      "<b>Tocá un pedido</b> y se abre el detalle de ese día: artículo por artículo, con las cantidades y el total. Los pagos no se tocan porque no tienen pedido atras.",
    ],
    tips: [
      "Cada pedido dice si era de <b>mañana</b> o de <b>tarde</b>: con dos pedidos el mismo día, es lo único que los distingue.",
      "Esta pantalla es <b>solo de lectura</b>: los pagos y las devoluciones los registra la confitería.",
      "Si ves algo que no cierra, avisales con el número de pedido a mano.",
    ],
  },
];

function bloqueInstalar(): string {
  return `
    <details class="tarjeta p-0" id="sec-instalar">
      <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
        <span class="text-masa-500">${iconoCampana}</span>
        <span class="flex-1">Instalar la app y recibir avisos</span>
      </summary>
      <div class="space-y-4 px-4 pb-4 text-sm text-masa-700">
        <p>
          Podés usar todo desde el navegador, pero si <b>agregas la app a la pantalla de inicio</b>
          se abre de una, ocupa toda la pantalla y —lo más importante— te pueden llegar los
          <b>avisos</b> cuando la confitería confirma tu pedido.
        </p>

        <p class="rounded-xl bg-crema-100 p-3">
          Actives o no las notificaciones del celular, la <b>campana</b> de arriba a la derecha te
          muestra siempre las novedades: cuando hay algo sin leer aparece un número, y tocandola
          entras a la lista y el número se apaga. Tocá un aviso y te lleva a lo que avisa.
          Con <b>Limpiar avisos</b> borras la lista; los pedidos no se tocan.
        </p>
        <p class="rounded-xl bg-crema-100 p-3">
          El botón de <b>Notificaciones del celular</b> se pone <b>verde</b> cuando los avisos te
          están llegando y <b>rojo</b> cuando no, así lo ves de una sin leer nada. Y con las
          notificaciones activadas, la pantalla que estás mirando se actualiza sola cuando llega el
          aviso: no hace falta salir y volver a entrar.
        </p>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="rounded-xl border border-crema-200 p-3">
            <h3 class="etiqueta">En Android (Chrome)</h3>
            <ol class="list-decimal space-y-1 pl-5">
              <li>Tocá los <b>tres puntitos</b> ⋮ arriba a la derecha.</li>
              <li>Elegí <b>Instalar aplicación</b> (o "Agregar a pantalla principal").</li>
              <li>Confirma. Queda el icono junto al resto de tus apps.</li>
            </ol>
          </div>
          <div class="rounded-xl border border-crema-200 p-3">
            <h3 class="etiqueta">En iPhone (Safari)</h3>
            <ol class="list-decimal space-y-1 pl-5">
              <li>Tocá el botón <b>Compartir</b> (el cuadradito con la flecha para arriba).</li>
              <li>Bajá y elegí <b>Agregar a inicio</b>.</li>
              <li>Tocá <b>Agregar</b>.</li>
            </ol>
            <p class="mt-2 rounded-lg bg-amber-50 p-2 text-amber-800">
              En el iPhone los avisos <b>solo funcionan con la app agregada a la pantalla de inicio</b>.
              Es una limitacion de Apple, no del sistema. Abrila desde ese icono, no desde Safari.
            </p>
          </div>
        </div>

        <div class="rounded-xl border border-crema-200 p-3">
          <h3 class="etiqueta">Activar los avisos</h3>
          <p class="mb-2">
            Abrí el menú <b>☰</b> arriba a la derecha y tocá <b>Notificaciones del celular</b> (en la
            computadora no hay menú ☰: el mismo botón está en la pantalla de la <b>campana</b>). El teléfono
            te va a preguntar si permitis las notificaciones: decí que si. Desde acá también podés:
          </p>
          <button id="activarAvisos" class="btn-primario">${iconoCampana} Activar avisos</button>
          <p id="estadoAvisos" class="mt-2 flex items-center gap-2 rounded-lg border px-3 py-2">
            <span id="estadoPunto" class="h-2.5 w-2.5 shrink-0 rounded-full"></span>
            <span id="estadoTexto">Revisando...</span>
          </p>
        </div>
      </div>
    </details>`;
}

function bloqueEntrar(): string {
  return `
    <details class="tarjeta p-0" id="sec-entrar">
      <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
        <span class="text-masa-500">${iconoRuta("#/clave")}</span>
        <span class="flex-1">Entrar a la app</span>
      </summary>
      <div class="space-y-3 px-4 pb-4 text-sm text-masa-700">
        <p>
          <b>No tenés clave.</b> Entrás tocando <b>Entrar con Google</b> y eligiendo la cuenta
          cuyo correo le pasaste a la confitería. Nada que acordarse ni que se pueda perder.
        </p>
        <ol class="list-decimal space-y-1 pl-5">
          <li>Abrí la app y tocá <b>Entrar con Google</b>.</li>
          <li>Google te muestra tus cuentas: elegí <b>la que le diste a la confitería</b>.</li>
          <li>Volvés solo a la app, ya adentro. Queda abierta por un mes en ese teléfono.</li>
        </ol>
        <p class="rounded-xl bg-amber-50 p-3 text-amber-800">
          Si te dice que <b>esa cuenta no tiene acceso</b>, casi siempre es que elegiste otra de las
          cuentas que tenés en el teléfono (la personal en vez de la del trabajo, por ejemplo).
          Tocá de nuevo y elegí la correcta. Si sigue sin dejarte, llamá a la confitería: capaz
          tienen tu correo mal escrito.
        </p>
        <p>
          La app <b>solo le pide a Google tu correo</b>, para saber que sos vos. No ve tus mails,
          tus contactos ni tus fotos, y la confitería tampoco.
        </p>
        <p>
          Hay <b>una sola sesión por cuenta</b>: si entrás en otro teléfono, en el primero se
          cierra. Si cambiás de teléfono no hay que avisar nada: entrás con Google y listo.
        </p>
      </div>
    </details>`;
}

export async function renderAyudaCliente(contenedor: HTMLElement): Promise<void> {
  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina flex items-center gap-2"><span class="text-masa-600">${iconoAyuda}</span>Cómo usar la app</h1>
      <p class="subtitulo">Todo lo que necesitás para pedir desde el celular. Las pantallas son de ejemplo.</p>
      <p class="mt-2 text-sm text-masa-600">Para alternar <b>Modo oscuro / Modo claro</b>, abrí el menú <b>☰</b> en el celular o el menú de tu cuenta en la computadora. La elección se guarda en cada dispositivo.</p>
    </div>

    <div class="mb-4 rounded-xl bg-crema-100 p-4 text-sm text-masa-700">
      <b>En resumen:</b> cargas tu pedido y lo mandas. Queda <b>pendiente</b> hasta que la confitería
      lo confirma — mientras tanto lo podés corregir o cancelar. Cuando lo confirman, entra a la
      producción del día y te llega el aviso.
    </div>

    <div class="space-y-3">
      ${SECCIONES.map(
        (s, i) => `
        <details id="sec-${i}" class="tarjeta p-0">
          <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
            <span class="text-masa-500">${iconoRuta(s.hash)}</span>
            <span class="flex-1">${s.titulo}</span>
          </summary>
          <div class="grid grid-cols-1 gap-4 px-4 pb-4 lg:grid-cols-2">
            <div>
              <p class="mb-3 text-sm text-masa-700">${s.queEs}</p>
              <h3 class="etiqueta">Paso a paso</h3>
              <ol class="mb-3 list-decimal space-y-1 pl-5 text-sm text-masa-800">
                ${s.pasos.map((p) => `<li>${p}</li>`).join("")}
              </ol>
              <h3 class="etiqueta">Bueno saber</h3>
              <ul class="list-disc space-y-1 pl-5 text-sm text-masa-600">
                ${s.tips.map((t) => `<li>${t}</li>`).join("")}
              </ul>
            </div>
            <div>
              <h3 class="etiqueta">Asi se ve la pantalla</h3>
              ${s.mock}
              <a href="${s.hash}" class="btn-sec mt-2 inline-flex text-xs">Ir a ${s.titulo} →</a>
            </div>
          </div>
        </details>`,
      ).join("")}
      ${bloqueInstalar()}
      ${bloqueEntrar()}
    </div>

    <div class="mt-6 rounded-xl border border-crema-200 p-4 text-sm text-masa-600">
      <b>¿Algo no funciona?</b> Llamá a la confitería. Si Google no te deja entrar, ellos revisan
      qué correo tenés cargado desde su pantalla de Clientes.
    </div>`;

  // Estado de los avisos + boton para activarlos. El permiso solo se puede
  // pedir dentro de un clic del usuario, por eso va como boton.
  const boton = contenedor.querySelector("#activarAvisos") as HTMLButtonElement;
  const caja = contenedor.querySelector("#estadoAvisos") as HTMLElement;
  const punto = contenedor.querySelector("#estadoPunto") as HTMLElement;
  const estadoTexto = contenedor.querySelector("#estadoTexto") as HTMLElement;

  const pintar = (estado: Awaited<ReturnType<typeof estadoAvisos>>): void => {
    const t = textoAvisos(estado);
    estadoTexto.textContent = `${t.titulo}: ${t.detalle}`;
    // El mismo verde/rojo que el boton del menu: es el mismo dato.
    const e = estiloAvisos(estado);
    caja.className = `mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 ${e.caja}`;
    punto.className = `h-2.5 w-2.5 shrink-0 rounded-full ${e.punto}`;
    boton.disabled = estado !== "desactivados";
    boton.classList.toggle("opacity-60", boton.disabled);
    if (estado === "instalar" && !estaInstalada()) {
      boton.textContent = "Primero agregala a la pantalla de inicio";
    }
  };

  pintar(await estadoAvisos());
  boton.addEventListener("click", async () => {
    estadoTexto.textContent = "Un momento...";
    try {
      pintar(await activarAvisos());
    } catch {
      pintar(await estadoAvisos());
    }
  });
}
